import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Play, Plus, Trash2 } from "lucide-react";
import {
  Alert,
  AlertDialog,
  Button,
  Dialog,
  DropdownMenu,
  Field,
  FieldLabel,
  IconButton,
  Typography,
} from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { SectionBoundary } from "./SectionBoundary";
import { useDeleteStayMedia, useStayMedia, useUploadStayMedia } from "./useStayMedia";
import { validateStayMediaDraft, type StayMediaItem } from "./stayMediaModel";

const localTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};

export function StayMediaSection({
  reservationId,
  petId,
  day,
  readOnly = false,
}: {
  reservationId: string;
  petId: string;
  day: string;
  readOnly?: boolean;
}) {
  const { t } = useTranslation("daycareHotel");
  const media = useStayMedia(reservationId, day);
  const upload = useUploadStayMedia();
  const remove = useDeleteStayMedia();
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [time, setTime] = useState(localTime);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<StayMediaItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<StayMediaItem | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);

  if (media.isLoading || media.isError) {
    return <SectionBoundary isLoading={media.isLoading} isError={media.isError} skeletonClassName="h-40 rounded-2xl" />;
  }

  const chooseFile = (file: File | undefined) => {
    if (!file) return;
    setDraftFile(file);
    setDescription("");
    setTime(localTime());
    setValidationError(null);
  };

  const clearDraft = () => {
    setDraftFile(null);
    setDescription("");
    setTime(localTime());
    setValidationError(null);
  };

  const save = async () => {
    if (!draftFile) return;
    const validation = validateStayMediaDraft({ file: draftFile, description, time });
    if (validation.ok === false) {
      setValidationError(`panel.mediaError.${validation.error}`);
      return;
    }
    try {
      await upload.mutateAsync({ reservationId, petId, day, time, file: draftFile, description });
      toast({ title: t("panel.mediaUploaded") });
      clearDraft();
    } catch (error) {
      const code = error instanceof Error ? error.message : "upload_failed";
      setValidationError(code.startsWith("panel.") ? code : `panel.mediaError.${code}`);
      toast({ variant: "destructive", title: t("panel.mediaUploadFailed") });
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await remove.mutateAsync({ id: deleteItem.id, storagePath: deleteItem.storagePath, reservationId, day });
      setDeleteItem(null);
      toast({ title: t("panel.mediaDeleted") });
    } catch {
      toast({ variant: "destructive", title: t("panel.mediaDeleteFailed") });
    }
  };

  return (
    <section>
      <div className="mb-2.5 flex items-center gap-2">
        <ImagePlus aria-hidden="true" size={18} className="text-akuna-blue" />
        <Typography variant="h5">{t("panel.sectionMedia")}</Typography>
        {(media.data ?? []).length > 0 ? <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">{(media.data ?? []).length}</span> : null}
        {!readOnly && <DropdownMenu
          label={t("panel.mediaAdd")}
          items={[
            { label: t("panel.mediaTakePhoto"), onSelect: () => photoInput.current?.click() },
            { label: t("panel.mediaRecordVideo"), onSelect: () => videoInput.current?.click() },
            { label: t("panel.mediaChooseGallery"), onSelect: () => galleryInput.current?.click() },
          ]}
          trigger={<button type="button" className="ml-auto inline-flex size-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"><Plus className="h-4 w-4" aria-hidden="true" /></button>}
        />}
      </div>
      <input ref={photoInput} className="sr-only" type="file" accept="image/*" capture="environment" aria-label={t("panel.mediaTakePhoto")} onChange={(event) => chooseFile(event.target.files?.[0])} />
      <input ref={videoInput} className="sr-only" type="file" accept="video/*" capture="environment" aria-label={t("panel.mediaRecordVideo")} onChange={(event) => chooseFile(event.target.files?.[0])} />
      <input ref={galleryInput} className="sr-only" type="file" accept="image/*,video/*" aria-label={t("panel.mediaChooseGallery")} onChange={(event) => chooseFile(event.target.files?.[0])} />

      {draftFile ? (
        <div className="mb-3 grid gap-2 rounded-xl border border-border p-3">
          <Typography variant="body-sm" className="font-medium">{draftFile.name}</Typography>
          <Field>
            <FieldLabel htmlFor="stay-media-description">{t("panel.mediaDescription")}</FieldLabel>
            <textarea id="stay-media-description" className="akuna-control min-h-20 w-full" value={description} onChange={(event) => setDescription(event.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="stay-media-time">{t("panel.mediaTime")}</FieldLabel>
            <input id="stay-media-time" type="time" className="akuna-control w-full" value={time} onChange={(event) => setTime(event.target.value)} />
          </Field>
          {validationError ? <Alert variant="danger">{t(validationError)}</Alert> : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={clearDraft}>{t("cancel")}</Button>
            <Button disabled={!description.trim() || !time || upload.isPending} loading={upload.isPending} onClick={() => void save()}>
              {t("panel.mediaUpload")}
            </Button>
          </div>
        </div>
      ) : null}

      {(media.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("panel.mediaEmpty")}</p>
      ) : (
        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
          {(media.data ?? []).map((item) => (
            <article key={item.id} className="group/media relative overflow-hidden rounded-lg border border-border bg-muted">
              <button type="button" className="block aspect-square w-full" title={`${item.time} · ${item.description}`} aria-label={t("panel.mediaOpen", { description: item.description })} onClick={() => setViewer(item)}>
                {item.mediaKind === "image" ? <img src={item.signedUrl} alt={item.description} className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center"><Play aria-hidden="true" size={20} /></span>}
              </button>
              {!readOnly && <IconButton variant="ghost" size="icon-sm" className="absolute right-0.5 top-0.5 bg-card/80 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/media:opacity-100 group-focus-within/media:opacity-100" label={t("panel.mediaDelete", { description: item.description })} onClick={() => setDeleteItem(item)}><Trash2 className="h-3.5 w-3.5" aria-hidden="true" /></IconButton>}
            </article>
          ))}
        </div>
      )}

      <Dialog
        className="stay-media-viewer-dialog"
        open={!!viewer}
        onOpenChange={(open) => { if (!open) setViewer(null); }}
        title={viewer ? <span className="sr-only">{viewer.description}</span> : t("panel.sectionMedia")}
      >
        {viewer ? (
          <figure className="grid h-full min-h-0 min-w-0 grid-rows-[minmax(0,1fr)_auto] gap-2">
            {viewer.mediaKind === "image" ? <img src={viewer.signedUrl} alt={viewer.description} className="h-full min-h-0 w-full object-contain" /> : <video src={viewer.signedUrl} controls className="h-full min-h-0 w-full" />}
            <figcaption className="min-w-0 w-full break-all text-sm leading-snug text-muted-foreground">
              {viewer.description}
            </figcaption>
          </figure>
        ) : null}
      </Dialog>

      <AlertDialog
        open={!!deleteItem}
        onOpenChange={(open) => { if (!open) setDeleteItem(null); }}
        title={t("panel.mediaDeleteTitle")}
        cancelLabel={t("cancel")}
        actionLabel={t("panel.mediaDeleteAction")}
        onAction={() => void confirmDelete()}
      >
        {t("panel.mediaDeleteDescription")}
      </AlertDialog>
    </section>
  );
}
